/**
 * AppDownloadBanner — Geräteabhängiger App-Download-Banner für die
 * öffentliche Buchungsseite (/buchen).
 *
 *  - iPhone/iPad  → Button "App Store"
 *  - Android      → Button "Google Play"
 *  - Desktop      → beide Buttons
 *
 * Die Store-Links kommen vom öffentlichen Endpoint
 * `/api/public/app-store-links` (Quelle: settings.app_store_url /
 * play_store_url mit Fallback-Defaults). Kein Login nötig.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Smartphone, Apple, PlayCircle } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const isIOS = () => /iPhone|iPad|iPod/i.test(window.navigator.userAgent);
const isAndroid = () => /Android/i.test(window.navigator.userAgent);
const isMobile = () => isIOS() || isAndroid();

const StoreButton = ({ href, Icon, label, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-transform active:scale-95 hover:opacity-90"
    style={{ backgroundColor: '#C9A227', color: '#000' }}
  >
    <Icon className="w-4 h-4" />
    {label}
  </a>
);

export const AppDownloadBanner = () => {
  const [links, setLinks] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/app-store-links`);
        if (!cancelled) setLinks(data);
      } catch (_e) {
        // Ohne Links kein Banner
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!links || (!links.ios_url && !links.android_url)) return null;

  const mobile = isMobile();
  const showIOS = (mobile ? isIOS() : true) && !!links.ios_url;
  const showAndroid = (mobile ? isAndroid() : true) && !!links.android_url;

  if (!showIOS && !showAndroid) return null;

  return (
    <div
      data-testid="booking-app-download-banner"
      className="rounded-xl p-3 sm:p-4 flex items-center gap-3 shadow-lg mb-6"
      style={{
        background: 'linear-gradient(135deg, #141310 0%, #2a2a1a 100%)',
        border: '1px solid #C9A227',
      }}
    >
      <div
        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
        style={{ backgroundColor: '#C9A227' }}
      >
        <Smartphone className="w-5 h-5 text-black" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-semibold text-sm">StrongFit App — noch schneller buchen</div>
        <div className="text-gray-300 text-xs mt-0.5">Termine, Punkte &amp; Erinnerungen direkt auf deinem Handy.</div>
      </div>
      <div className="flex-shrink-0 flex items-center gap-2">
        {showIOS && (
          <StoreButton href={links.ios_url} Icon={Apple} label="App Store" testid="booking-app-download-ios" />
        )}
        {showAndroid && (
          <StoreButton href={links.android_url} Icon={PlayCircle} label="Google Play" testid="booking-app-download-android" />
        )}
      </div>
    </div>
  );
};

export default AppDownloadBanner;
